/**
 * InsertList ("הכנסת רשימת משתמשים") — bulk-import customers from pasted text.
 * Opened from the Customers screen's "⋯" menu. Logic unchanged from the pre-redesign
 * version; only the UI moved to the shared kit (a Modal instead of a gray overlay).
 *
 * Flow
 *   1. Paste lines "שם,טלפון,היסטוריה" (tabs from Excel/Sheets become commas),
 *      optionally choose a status and tags for everyone.
 *   2. "בדיקת שורות" normalises phones (+972/972/dashes → 05…) and shows each line
 *      with its validation result; invalid lines are red and can be fixed inline.
 *   3. "הכנס לקוחות" imports the valid lines one by one via insertOrUpdateCustomers,
 *      merging each customer's existing tags (getCusTagsByMailOrPhone) with the chosen ones.
 *
 * Props: close() — closes the dialog.
 */
import React, { useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';

import * as actionsCreators from './../../actions/index.js';
import { Modal, Button, Input, Select, Field, FormGrid, LIST, useLookupList, toOptions, itemLabel } from '../../ui';
import validationPhoneNumber from './../../utils/validationIsraelyPhone.js';
import classes from './insertList.css';

const TEXTEREA_MOD = 'textarea',
  LIST_MOD = 'list';

const InsertList = (props) => {
  const [text, setText] = useState('');
  const [mod, setMod] = useState(TEXTEREA_MOD);
  const [status, setStatus] = useState('');
  const [statusId, setStatusId] = useState(null);
  const [tags, setTags] = useState([]); // Array of {id, name} objects
  const [tagIds, setTagIds] = useState([]); // Array of tag IDs
  const [importing, setImporting] = useState(false);

  const statusList = useLookupList(LIST.STATUS);
  const tagList = useLookupList(LIST.TAG);

  const getCusTags = async (phone, mail) => {
    // Returns array of {id, name} objects
    let originalTags = await props.getCusTagsByMailOrPhone(phone, mail);
    if (!originalTags || originalTags.length === 0) return { tagIds: [], tagNames: [] };

    const tagIds = originalTags.map((t) => t.id);
    const tagNames = originalTags.map((t) => t.name);
    return { tagIds, tagNames };
  };

  const insertList = async () => {
    let lines = text.split('\n').filter((line) => checkLine(line) === 'תקין');

    props.showAlert('מנסה להכניס ' + lines.length + ' לקוחות למערכת');
    setImporting(true);

    for (const line of lines) {
      let splitLine = line.split(',');
      let [firstname, ...restName] = splitLine[0].split(' ');
      let phone = splitLine[1] || '';
      let history = splitLine[2] || '';

      // Get existing tags for this customer
      const existingTags = await getCusTags(phone, null);

      // Merge tag IDs (selected + existing, deduplicated)
      const mergedTagIds = [...new Set([...tagIds, ...existingTags.tagIds])];

      // Merge tag names for intrestedIn field
      const selectedTagNames = tags.map((t) => t.name || t.val || t);
      const mergedTagNames = [...new Set([...selectedTagNames, ...existingTags.tagNames])];

      // Build customer data - only include fields with actual values
      const customerData = {
        id: '',
        firstname: firstname + '' || '',
        lastname: restName.join(' ') || '',
        phone: phone,
        notes: [],
        history: [{ val: history, dateTime: Date.now(), creatorName: 'system (listInsert)', update: true }],
      };

      if (status && status !== '') customerData.status = status;
      if (statusId) customerData.status_id = statusId;
      if (mergedTagIds.length > 0) customerData.tag_ids = mergedTagIds;
      if (mergedTagNames.length > 0) customerData.intrestedIn = mergedTagNames.join(',');

      await props.insertOrUpdateCustomers(customerData);
    }
    setImporting(false);
    props.showAlert('הכנסת הרשימה למערכת הסתיימה');
  };

  const fixlinesAndChangeMod = () => {
    let textList = text.split('\t').join(',').split('\n');
    textList = textList.map((line) => {
      let splitLine = line.split(',');
      if (splitLine.length < 3) return line;
      splitLine[1] = splitLine[1].split('+972').join('0');
      splitLine[1] = splitLine[1].split('972').join('0');
      splitLine[1] = splitLine[1].split('-').join('');
      splitLine[1] = splitLine[1].trim();
      if (splitLine[1].length === 9) splitLine[1] = '0' + splitLine[1];

      return splitLine.join(',');
    });
    setText(textList.join('\n'));
    setMod(LIST_MOD);
  };

  const changeLine = (val, line) => {
    let textList = text.split('\n');
    textList[line] = val;
    textList = textList.filter((line) => line !== '');
    setText(textList.join('\n'));
  };

  const checkLine = (line) => {
    let splitLine = line.split(',');
    if (splitLine.length !== 3) return 'מספר השורות לא תקין';
    if (splitLine[0] === '') return 'השם ריק';
    if (splitLine[1] !== '' && !validationPhoneNumber(splitLine[1])) return 'טלפון לא תקין';
    if (!splitLine[2] && splitLine[2] === '') return 'יש לרשום היסטוריה כלשהיא';
    return 'תקין';
  };

  const reset = () => {
    setMod(TEXTEREA_MOD);
    setText('');
    setTags([]);
    setTagIds([]);
    setStatus('');
    setStatusId(null);
  };

  const lines = text.split('\n');
  const validCount = mod === LIST_MOD ? lines.filter((l) => checkLine(l) === 'תקין').length : 0;

  return (
    <Modal
      open
      width={760}
      title="הכנסת רשימת לקוחות למערכת"
      onCancel={() => props.close()}
      maskClosable={false}
      footer={
        <div className={classes.footer}>
          <Button onClick={reset}>איפוס</Button>
          <span className={classes.spacer} />
          <Button onClick={() => props.close()}>ביטול</Button>
          {mod === TEXTEREA_MOD ? (
            <Button type="primary" onClick={fixlinesAndChangeMod} disabled={!text.trim()}>
              בדיקת שורות
            </Button>
          ) : (
            <Button type="primary" onClick={insertList} loading={importing} disabled={validCount === 0}>
              הכנס {validCount} לקוחות
            </Button>
          )}
        </div>
      }
    >
      <p className={classes.hint}>
        שלוש עמודות בדיוק, בסדר הזה: <strong>שם, טלפון, היסטוריה</strong>. אפשר להדביק ישירות מאקסל.
      </p>

      <FormGrid>
        <Field label="סטטוס לכולם">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="ללא שינוי"
            options={toOptions(statusList.items)}
            value={statusId || undefined}
            onChange={(id, opt) => {
              setStatusId(id || null);
              setStatus(opt ? itemLabel(opt.item) : '');
            }}
          />
        </Field>
        <Field label="טאגים לכולם">
          <Select
            mode="multiple"
            allowClear
            optionFilterProp="label"
            placeholder="בחר טאגים"
            options={toOptions(tagList.items)}
            value={tagIds}
            onChange={(ids, opts) => {
              setTagIds(ids);
              setTags(opts.map((o) => ({ id: o.value, name: itemLabel(o.item) })));
            }}
          />
        </Field>
      </FormGrid>

      <div className={classes.editor}>
        {mod === TEXTEREA_MOD ? (
          <Input.TextArea
            rows={14}
            value={text}
            placeholder={'דנה כהן,0501234567,נרשמה בכנס\nיוסי לוי,0527654321,התעניין בקורס'}
            onChange={(e) => setText(e.target.value)}
          />
        ) : (
          <>
            <div className={classes.summary}>
              {validCount} שורות תקינות מתוך {lines.length}
            </div>
            <div className={classes.lines}>
              {lines.map((line, i) => {
                const lineAlert = checkLine(line);
                const ok = lineAlert === 'תקין';
                return (
                  <div key={i} className={classes.line}>
                    {ok ? (
                      <CheckCircleFilled className={classes.ok} />
                    ) : (
                      <ExclamationCircleFilled className={classes.bad} />
                    )}
                    <Input
                      size="small"
                      status={ok ? undefined : 'error'}
                      value={line}
                      onChange={(e) => changeLine(e.target.value, i)}
                    />
                    {!ok ? <span className={classes.reason}>{lineAlert}</span> : null}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const mapStateToProps = () => {
  return {};
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showAlert: actionsCreators.showAlert,
      insertOrUpdateCustomers: actionsCreators.insertOrUpdateCustomers,
      getCusTagsByMailOrPhone: actionsCreators.getCusTagsByMailOrPhone,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(InsertList);
